import { Fragment, useMemo } from "react";
import { useDndContext } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, ChevronRight, Eye, X } from "lucide-react";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { MaskBadge } from "@/components/ui/mask-badge";
import { Button } from "@/components/ui/button";
import { WaferProgressIndicator } from "@/components/shared/WaferProgressIndicator";
import {
  formatISOToReadableDate,
  formatMinutesToDuration,
  getDurationInMinutes,
} from "@/utils/timeFormatting";
import Tooltip from "@/components/shared/Tooltip";
import { TruncatedCell } from "@/components/shared/TruncatedCell";
import { PlanningMeasurementRequest } from "@/types/planning";
import { abbreviateName } from "@/lib/utils";
import { useIsAdmin, usePermissionContext } from "@/permissions";
import { isPlanningKey, useMarkWaferRequestAsToSchedule } from "@/hooks/useRequestPlanning";
import { isWaferRequest } from "../MeasurementRequestsPlanningTable.functions";
import { DragHandle } from "./DragHandle";
import { ExpandedMeasurementRequestRow } from "./ExpandedMeasurementRequestRow";

type DraggableRowProps = {
  expandedRows: Set<number>;
  onWaferSelectionChange: (selection: Set<string>) => void;
  row: PlanningMeasurementRequest;
  selectedWafers: Set<string>;
  showCheckboxes: boolean;
  status: "MEASURED" | "SCHEDULED";
  toggleRow: (requestId: number) => void;
  waferProgressPerRequest: Map<number, { backlog: number; scheduled: number; measured: number }>;
  waferRequestIds: number[];
};
export function DraggableRow({
  expandedRows,
  onWaferSelectionChange,
  row,
  selectedWafers,
  showCheckboxes,
  status,
  toggleRow,
  waferProgressPerRequest,
  waferRequestIds,
}: DraggableRowProps) {
  const { active } = useDndContext();
  const allowDragging = useIsAdmin();
  const { hasPermission } = usePermissionContext();
  const canDeschedule = hasPermission("measurement_requests", "update");
  const { mutate } = useSWRConfig();
  const { trigger: markAsToSchedule, isMutating } = useMarkWaferRequestAsToSchedule();
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    disabled: !allowDragging || status === "MEASURED",
    id: row.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRequestSelected = (requestId: number) => {
    return (
      row.waferRequests.length > 0 &&
      row.waferRequests.every((wafer) => selectedWafers.has(`${requestId}-${wafer._id}`))
    );
  };

  const handleRequestSelection = (requestId: number, checked: boolean) => {
    const newSelected = new Set(selectedWafers);

    row.waferRequests.forEach((wafer) => {
      const waferKey = `${requestId}-${wafer._id}`;
      if (checked) {
        newSelected.add(waferKey);
      } else {
        newSelected.delete(waferKey);
      }
    });

    onWaferSelectionChange(newSelected);
  };

  // Calculate total time for all wafers in this measurement request
  const totalTime = useMemo(() => {
    if (status === "MEASURED") {
      // For measured wafers, sum the actual durations
      return row.waferRequests.reduce((labelTotal, wafer) => {
        if (
          wafer.status === "SCHEDULED" ||
          wafer.status === "TO_SCHEDULE" ||
          wafer.status === "MEASURED" ||
          wafer.status === "MEASURING"
        ) {
          return labelTotal;
        }
        const actualDuration = getDurationInMinutes(wafer.startedAt, wafer.completedAt);
        return labelTotal + actualDuration;
      }, 0);
    }
    // For scheduled wafers, use estimated time
    return row.waferRequests.reduce((labelTotal, wafer) => {
      return labelTotal + wafer.estimatedTime * wafer.dieCount;
    }, 0);
  }, [row.waferRequests, status]);

  // Find the wafer with the highest priority (latest finish time)
  const latestFinishTime = useMemo(() => {
    let highestPriority = -1;
    let latestTime: string | null = null;

    row.waferRequests.forEach((wafer) => {
      if (wafer.priority !== null && wafer.priority > highestPriority && wafer.estFinishTime) {
        highestPriority = wafer.priority;
        latestTime = wafer.estFinishTime;
      }
    });

    return latestTime;
  }, [row.waferRequests]);

  const displayExpandedRows = !isDragging && (!active || isWaferRequest(active.id));

  const handleDescheduleRequest = async () => {
    const scheduledWafers = row.waferRequests.filter((wafer) => wafer.status === "SCHEDULED");
    if (scheduledWafers.length === 0) return;

    try {
      for (const wafer of scheduledWafers) {
        await markAsToSchedule({
          measurement_request_id: row.id,
          wafer_id: wafer._id,
        });
      }
      toast.success("Successfully unscheduled all wafers in the measurement request!");
    } catch (error) {
      toast.error(
        `Error: ${error instanceof Error ? error.message : "Unable to unschedule wafers"}`,
      );
    } finally {
      await mutate(isPlanningKey);
    }
  };

  return (
    <Fragment key={row.id}>
      <TableRow
        ref={setNodeRef}
        style={style}
        className="cursor-pointer hover:bg-muted/50 h-10"
        onClick={(e) => {
          // Don't toggle row when not clicking on a checkbox
          if (!(e.target as HTMLElement).closest('[role="checkbox"]')) {
            toggleRow(row.id);
          }
        }}
      >
        {isDragging ? (
          <>
            <TableCell className="bg-yellow-500/30" />
            <TableCell className="bg-yellow-500/30" />
            <TableCell className="bg-yellow-500/30" />
            <TableCell className="bg-yellow-500/30 max-w-68">
              <TruncatedCell text={row.name} />
            </TableCell>
            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
              {row.imecLotId || "/"}
            </TableCell>
            <TableCell
              className="h-10 py-1 text-sm bg-yellow-500/30"
              onClick={(e) => e.stopPropagation()}
            >
              <MaskBadge maskName={row.mask.name} />
            </TableCell>
            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">{row.project.name}</TableCell>

            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
              {abbreviateName(row.createdBy)}
            </TableCell>
            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
              {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "---"}
            </TableCell>
            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
              {totalTime > 0 ? formatMinutesToDuration(totalTime) : "---"}
            </TableCell>
            {status === "SCHEDULED" && (
              <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
                {formatISOToReadableDate(latestFinishTime)}
              </TableCell>
            )}
            <TableCell className="h-10 py-1 text-sm bg-yellow-500/30">
              {(() => {
                const progress = waferProgressPerRequest.get(row.id);
                return progress ? (
                  <WaferProgressIndicator
                    backlog={progress.backlog}
                    scheduled={progress.scheduled}
                    measured={progress.measured}
                  />
                ) : (
                  "---"
                );
              })()}
            </TableCell>
            <TableCell className="bg-yellow-500/30" />
          </>
        ) : (
          <>
            {status !== "MEASURED" && (
              <TableCell>
                {allowDragging ? <DragHandle {...attributes} {...listeners} /> : null}
              </TableCell>
            )}
            {showCheckboxes && (
              <TableCell className="h-10 py-1 w-px pl-0 pr-2" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  disabled={!allowDragging}
                  checked={isRequestSelected(row.id)}
                  onCheckedChange={(checked) => handleRequestSelection(row.id, checked as boolean)}
                />
              </TableCell>
            )}
            <TableCell className="h-10 py-1">
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {expandedRows.has(row.id) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </TableCell>
            <TableCell className="font-medium h-10 py-1 text-sm max-w-68">
              <TruncatedCell text={row.name} />
            </TableCell>
            <TableCell className="h-10 py-1 text-sm">{row.imecLotId || "/"}</TableCell>
            <TableCell className="h-10 py-1 text-sm" onClick={(e) => e.stopPropagation()}>
              <MaskBadge maskName={row.mask.name} />
            </TableCell>
            <TableCell className="h-10 py-1 text-sm max-w-36 truncate">
              {row.testLabel.measurementCapability?.name}
            </TableCell>
            <TableCell className="h-10 py-1 text-sm max-w-20">
              <TruncatedCell text={abbreviateName(row.createdBy)} />
            </TableCell>
            <TableCell className="h-10 py-1 text-sm">
              {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "---"}
            </TableCell>
            <TableCell className="h-10 py-1 text-sm">
              {totalTime > 0 ? formatMinutesToDuration(totalTime) : "---"}
            </TableCell>
            {status === "SCHEDULED" && (
              <TableCell className="h-10 py-1 text-sm">
                {formatISOToReadableDate(latestFinishTime)}
              </TableCell>
            )}
            <TableCell className="h-10 py-1 text-sm">
              {(() => {
                const progress = waferProgressPerRequest.get(row.id);
                return progress ? (
                  <WaferProgressIndicator
                    backlog={progress.backlog}
                    scheduled={progress.scheduled}
                    measured={progress.measured}
                  />
                ) : (
                  "---"
                );
              })()}
            </TableCell>
            <TableCell className="h-10 py-1">
              <Tooltip
                asChild
                trigger={
                  <Link to={`/measurement-requests/${row.id}`}>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                }
                content="Go to details page"
              />
              {status === "SCHEDULED" && canDeschedule ? (
                <Tooltip
                  asChild
                  trigger={
                    <Button
                      aria-label="Unschedule all wafers in measurement request"
                      className="h-6 w-6 p-0"
                      disabled={isMutating}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDescheduleRequest();
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  }
                  content="Unschedule all wafer requests"
                />
              ) : null}
            </TableCell>
          </>
        )}
      </TableRow>
      {expandedRows.has(row.id) &&
        row.waferRequests &&
        row.waferRequests.length > 0 &&
        displayExpandedRows && (
          <ExpandedMeasurementRequestRow
            key={`${row.id}-expanded`}
            onWaferSelectionChange={onWaferSelectionChange}
            request={row}
            selectedWafers={selectedWafers}
            showCheckboxes={showCheckboxes}
            status={status}
            waferRequestIds={waferRequestIds}
          />
        )}
    </Fragment>
  );
}
